import { memoize } from 'lodash';
import { FeatureToggles, FeatureToggleValues } from '../model/feature-toggles.model';
import { createSelector } from '@ngrx/store';
import { selectAllFeatureToggles } from './feature-toggles.selectors';
import { EditorSettings } from 'tinymce';
import { selectMessageConversationTypeById } from 'common/store/genesys-cloud/conversations/messages/selectors/message-conversation.selectors';
import { MessageType } from 'common/store/genesys-cloud/conversations/common/models/message-type.enum';
import { ContentFormat } from 'shared/components/genesys-editor/content-format.model';
import { EnabledProvider } from 'environments/models/enabled-provider.model';

export interface FooterSettings {
  editorSettings: Partial<EditorSettings>;
  contentFormat: ContentFormat;
  richTextEnabled: boolean;
}

function getRichTextEditorSettings(insertImageEnabled: boolean): Partial<EditorSettings> {
  return {
    toolbar: insertImageEnabled
      ? 'bold italic strikethrough fileUpload insertImage'
      : 'bold italic strikethrough fileUpload',
    formats: {
      strikethrough: { inline: 's', exact: true }
    }
  };
}

const regularEditorSettings: Partial<EditorSettings> = {
  toolbar: 'fileUpload'
};

export function getRichTextFooterSettings(insertImageEnabled: boolean): FooterSettings {
  return {
    editorSettings: getRichTextEditorSettings(insertImageEnabled),
    contentFormat: 'html',
    richTextEnabled: true
  };
}

export const regularFooterSettings: FooterSettings = {
  editorSettings: regularEditorSettings,
  contentFormat: 'text',
  richTextEnabled: false
};

const selectIsRichTextFeatureToggleEnabledForChannelType = memoize((conversationId: string) =>
  createSelector(
    selectAllFeatureToggles(),
    selectMessageConversationTypeById(conversationId),
    (featureToggles, conversationType) => isTextFormattingEnabled(featureToggles, conversationType)
  )
);

export const selectFooterSettings = memoize((conversationId: string, enabledProviders: EnabledProvider[]) =>
  createSelector(
    selectMessageConversationTypeById(conversationId),
    selectIsRichTextFeatureToggleEnabledForChannelType(conversationId),
    (conversationType, enabled) => {
      const urlImageEnabledProviders = enabledProviders.filter((e) => e.isUrlImageEnabled);
      const insertImageEnabled = isAllowedForConversationType(conversationType, urlImageEnabledProviders);
      return isAllowedForConversationType(conversationType, enabledProviders) && enabled
        ? getRichTextFooterSettings(insertImageEnabled)
        : regularFooterSettings;
    }
  )
);

export const selectIsUrlImagesEnabled = memoize((conversationId: string, enabledProviders: EnabledProvider[]) =>
  createSelector(
    selectMessageConversationTypeById(conversationId),
    selectIsRichTextFeatureToggleEnabledForChannelType(conversationId),
    (conversationType, enabled) => {
      const urlImageEnabledProviders = enabledProviders.filter((e) => e.isUrlImageEnabled);
      return isAllowedForConversationType(conversationType, urlImageEnabledProviders) && enabled;
    }
  )
);

export const selectIsRichTextEnabled = memoize((conversationId: string, enabledProviders: EnabledProvider[]) =>
  createSelector(selectFooterSettings(conversationId, enabledProviders), (settings) => settings.richTextEnabled)
);

function isAllowedForConversationType(type: MessageType, enabledProviders: EnabledProvider[]): boolean {
  return enabledProviders.map((e) => e.type).includes(type);
}

function isTextFormattingEnabled(
  featureToggles: FeatureToggleValues,
  conversationType: MessageType
): boolean | undefined {
  switch (conversationType) {
    case MessageType.WebMessaging:
      return featureToggles[FeatureToggles.ChatTextFormatting];
    case MessageType.Facebook:
      return featureToggles[FeatureToggles.FacebookMessengerTextFormatting];
    case MessageType.WhatsApp:
      return featureToggles[FeatureToggles.WhatsAppTextFormatting];
    default:
      return false;
  }
}
